import React, { useMemo } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Typography } from "@material-ui/core";
import { Delete, DragIndicator, ExpandMore } from "@material-ui/icons";

type Props = {
  id: string;
  label: string;
  children: React.ReactNode;
  onRemove?: () => void;
  accordion?: boolean;
  disabled?: boolean;
};

export const SortableItem: React.FC<Props> = ({
  id,
  label,
  children,
  onRemove,
  accordion = false,
  disabled = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = useMemo<React.CSSProperties>(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }),
    [transform, transition, isDragging]
  );

  const controls = (
    <Box display="flex" alignItems="center" ml="auto">
      {onRemove && (
        <IconButton
          size="small"
          disabled={disabled}
          aria-label={`Remove ${label}`}
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <Delete fontSize="small" />
        </IconButton>
      )}
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={`Reorder ${label}`}
        {...attributes}
        {...listeners}
        style={grabStyle}
      >
        <DragIndicator fontSize="small" />
      </IconButton>
    </Box>
  );

  if (accordion) {
    return (
      <div ref={setNodeRef} style={style}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" width="100%">
              <Typography variant="subtitle1">{label}</Typography>
              {controls}
            </Box>
          </AccordionSummary>
          <AccordionDetails style={accordionDetailsStyle}>{children}</AccordionDetails>
        </Accordion>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <Box display="flex" alignItems="flex-start" width="100%" mb={1}>
        <Box flexGrow={1}>{children}</Box>
        {controls}
      </Box>
    </div>
  );
};

const grabStyle: React.CSSProperties = { cursor: "grab" };
const accordionDetailsStyle: React.CSSProperties = { flexDirection: "column", alignItems: "stretch" };
